import { useEffect, useMemo, useRef, useState } from 'react';
import { Location, useLocation, useSearchParams } from 'react-router-dom';
import {
  NavigationCustomizationDataDtoResponse,
  NavigationMenuItemDto,
  NavigationPageItemDto,
  PageDto,
} from '@asset-metrix/portal-gateway-client-js';
import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { v4 as uuid } from 'uuid';

import {
  createNavigationCustomization,
  fetchNavigationCustomization,
} from '../api/portalGateway/navigationCustomization';
import { NO_ROLE_ID } from '../api/portalGatewayClient';
import { useStore } from '../store';
import { matchesComparisonNavSession } from '../utils/comparisonDetailPageNav';
import { COMPARISON_ENTITIES_URL_PARAM } from '../utils/constants';
import { DetailPageNavigationState } from '../utils/detailPageLinkDisplay';
import { getDetailPageNavigationIdentity } from '../utils/detailPageNavigationIdentity';
import { prependRoleId } from '../utils/prependRoleId';
import { resolveDetailPageNavItemDisplay } from '../utils/resolveDetailPageNavItemDisplay';
import { pageWithRoleLink } from '../utils/urlPath';

// create a new dynamic navigation item if not already present
export const useCreateDynamicNavItem = (
  navigationData?: NavigationMenuItemDto | NavigationPageItemDto,
  pageData?: PageDto
) => {
  const roleId = useStore((state) => state.activeUserRole?.id);
  const queryClient = useQueryClient();
  const createMutation = useCreateNavigationCustomizationMutation();
  const [urlSearchParams] = useSearchParams();
  const { state }: Location<DetailPageNavigationState | null> = useLocation();

  const navigationCustomizationData:
    | NavigationCustomizationDataDtoResponse
    | undefined = queryClient.getQueryData(
    prependRoleId(['navigationCustomization'])
  );

  // Pure lookup: find the existing dynamic nav item by matching hidden
  // page filters identity within the same detail root page.
  const existingItemId = useMemo(() => {
    const currentIdentity = getDetailPageNavigationIdentity(
      urlSearchParams,
      pageData
    );
    if (
      navigationData == null ||
      navigationCustomizationData == null ||
      navigationData.type !== 'navigation-page-item' ||
      currentIdentity == null
    ) {
      return null;
    }

    const rootSlug = navigationData.completeSlug.split('/')[0];
    const currentPageHref = pageWithRoleLink(roleId ?? NO_ROLE_ID, {
      pageType: 'detailPage',
      slug: navigationData.completeSlug,
    });

    // For comparison pages, the "session identity" is the target page slug plus the
    // entity pool (dp_entities), not the specific pair currently selected. Match by
    // complete slug + sorted pool so that changing the pair does not produce a false
    // "no match" and trigger a duplicate nav item creation.
    const isComparisonPage = urlSearchParams.has(COMPARISON_ENTITIES_URL_PARAM);

    const item = navigationCustomizationData.data.dynamicNavigationItems.find(
      (di) => {
        if (isComparisonPage) {
          return matchesComparisonNavSession(
            di.href,
            new URL(currentPageHref, 'http://localhost').pathname,
            urlSearchParams
          );
        }

        const dynamicItemUrlSearch = new URLSearchParams(
          di.href.split('?').pop()
        );
        const dynamicIdentity = getDetailPageNavigationIdentity(
          dynamicItemUrlSearch,
          pageData
        );

        return (
          di.href.includes(rootSlug) &&
          dynamicIdentity?.signature === currentIdentity.signature
        );
      }
    );

    return item?.id ?? null;
  }, [
    navigationData,
    pageData,
    navigationCustomizationData,
    roleId,
    urlSearchParams,
  ]);

  // Track the id of an item we created ourselves (before the optimistic
  // update makes it show up in existingItemId on the next render).
  const [pendingItemId, setPendingItemId] = useState<string | null>(null);

  // Access mutation and location state via refs so the effect doesn't
  // re-trigger on every mutation state change or location update.
  const createMutationRef = useRef(createMutation);
  createMutationRef.current = createMutation;
  const locationStateRef = useRef(state);
  locationStateRef.current = state;

  useEffect(() => {
    if (existingItemId != null) {
      setPendingItemId(null);
      return;
    }

    const currentIdentity = getDetailPageNavigationIdentity(
      urlSearchParams,
      pageData
    );

    if (
      navigationData == null ||
      navigationCustomizationData == null ||
      navigationData.type !== 'navigation-page-item' ||
      currentIdentity == null
    ) {
      return;
    }

    if (createMutationRef.current.isPending) {
      return;
    }

    let href = pageWithRoleLink(roleId ?? NO_ROLE_ID, {
      pageType: 'detailPage',
      slug: navigationData.completeSlug,
    });
    const persistedSearchString =
      currentIdentity.persistedSearchParams.toString();
    if (persistedSearchString.length > 0) {
      href = `${href}?${persistedSearchString}`;
    }

    const navItemLabels = resolveDetailPageNavItemDisplay(
      pageData,
      urlSearchParams,
      {
        detailsDiscriminatorDisplayValue:
          locationStateRef.current?.detailsDiscriminatorDisplayValue,
      }
    );

    const newId = uuid();
    setPendingItemId(newId);
    createMutationRef.current.mutate({
      data: {
        id: newId,
        href,
        icon: navigationData.icon ?? 'draft',
        originHref: locationStateRef.current?.originHref,
        navItemLabels,
      },
    });
  }, [
    existingItemId,
    navigationData,
    pageData,
    roleId,
    navigationCustomizationData,
    urlSearchParams,
  ]);

  return existingItemId ?? pendingItemId;
};

function useCreateNavigationCustomizationMutation() {
  const options = queryOptions({
    queryKey: prependRoleId(['navigationCustomization']),
    queryFn: fetchNavigationCustomization,
  });
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createNavigationCustomization,
    onMutate: async (newNavItem) => {
      await queryClient.cancelQueries(options);
      const previousNavigationCustomization = queryClient.getQueryData(
        options.queryKey
      );

      // optimistically update to the new value
      queryClient.setQueryData(options.queryKey, (old) =>
        old
          ? {
              ...old,
              data: {
                dynamicNavigationItems: [
                  ...old.data.dynamicNavigationItems,
                  newNavItem.data,
                ],
              },
            }
          : undefined
      );

      return { previousNavigationCustomization };
    },
    onError: (error, variables, context) => {
      queryClient.setQueryData(
        options.queryKey,
        context?.previousNavigationCustomization
      );
    },
    // no need to handle onSettled - nothing changes on success!
  });
}
